// src/pages/Layouts/Products/ProductDetail/ProductDetail.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import DOMPurify from "dompurify";
import {
  ChevronLeft,
  ChevronRight,
  Minus,
  Plus,
  ArrowLeft,
  BadgePercent,
  PackageX,
  Truck,
  ShieldCheck,
  RotateCcw,
  MessageCircle,
} from "lucide-react";
import { useTheme } from "../../../../context/ThemeContext";
import { getProductDetail } from "../../../../api/product";
import { useCart } from "../../../../context/CartContext/CartContext";
import ProductReviews, { Stars } from "./ProductReviews";
import FavoriteButton from "../../../../components/Common/FavoriteButton/FavoriteButton";
import axiosInstance from "../../../../api/axiosInstance";

/* ───────────────── Types ───────────────── */
type Feature = {
  id: number;
  ozellikAdi: string;
  ozellikDegeri: string;
  siraNo?: number;
};

type ProductDetailDto = {
  id: number;
  ad: string;

  paraBirimi?: string | null;

  // pricing
  fiyat: number;
  listeFiyati?: number | null;
  indirimliFiyat?: number | null;
  satisFiyati?: number | null;
  indirimAktifMi?: boolean | null;

  // stock
  stokAdedi?: number | null;

  // media
  fotoMain?: string | null;
  fotoList?: string[] | null;

  // content
  aciklama?: string | null;
  ozellikler?: Feature[] | null;

  // optional cart integration fields (varsa)
  varyantId?: number | null;
  fiyatNet?: number | null;
  vergiOraniYuzde?: number | null;
  secilenSecenekOzet?: string | null;
};

/* ───────────────── Helpers ───────────────── */
function money(v: number, currency: string) {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(v);
}

function computePricing(d: ProductDetailDto) {
  const currency = d.paraBirimi || "TRY";
  const normal = typeof d.fiyat === "number" ? d.fiyat : 0;

  const disc =
    typeof d.indirimliFiyat === "number" && d.indirimliFiyat > 0
      ? d.indirimliFiyat
      : null;

  const isDiscount =
    (d.indirimAktifMi ?? true) && disc != null && normal > 0 && disc < normal;

  const sale = isDiscount ? (disc as number) : normal;

  const pct =
    isDiscount && normal > 0 ? Math.round(((normal - sale) / normal) * 100) : null;

  return {
    currency,
    normal,
    sale,
    isDiscount,
    pct,
    normalText: money(normal, currency),
    saleText: money(sale, currency),
  };
}

/* ====== Theme helpers ====== */
const hexToRgb = (hex: string) => {
  const h = (hex || "#ffffff").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

/* ====== Rich text sanitize ====== */
function sanitizeHtml(html?: string | null) {
  const raw = (html || "").trim();
  if (!raw) return "";

  return DOMPurify.sanitize(raw, {
    USE_PROFILES: { html: true },
    ALLOWED_TAGS: [
      "b",
      "strong",
      "i",
      "em",
      "u",
      "p",
      "br",
      "div",
      "span",
      "ul",
      "ol",
      "li",
      "h1",
      "h2",
      "h3",
      "h4",
      "blockquote",
      "a",
    ],
    ALLOWED_ATTR: ["href", "target", "rel"],
  });
}

type TabKey = "desc" | "delivery" | "installments" | "comments";

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const { theme } = useTheme();
  const { addItem, loading: cartLoading } = useCart();

  const accent = theme?.accent ?? "#2563eb";
  const textPrimary = theme?.textPrimary ?? "#0f172a";
  const textSecondary = theme?.textSecondary ?? "#475569";

  const pageBg = theme?.backgroundColor || "#ffffff";
  const bgRgb = hexToRgb(pageBg);
  const isDarkBg = luminance(bgRgb) < 0.5;

  const cardBg = isDarkBg ? "rgba(17,24,39,0.92)" : "#ffffff";
  const borderCol = isDarkBg ? "rgba(255,255,255,.14)" : "rgba(15,23,42,.12)";
  const softShadow = isDarkBg ? "0 12px 32px rgba(0,0,0,.40)" : "0 12px 32px rgba(2,6,23,.10)";

  const cardStyle: React.CSSProperties = {
    backgroundColor: cardBg,
    borderColor: borderCol,
    borderWidth: 1,
    boxShadow: softShadow,
  };

  const subtleBorder: React.CSSProperties = { borderColor: borderCol };

  /* ───────────────── State ───────────────── */
  const [data, setData] = useState<ProductDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string>();
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("desc");
  const [reviewOzet, setReviewOzet] = useState<{ ortalama: number; toplamAdet: number } | null>(null);

  /* ───────────────── Rating özeti fetch (sayfa yüklenince) ───────────────── */
  useEffect(() => {
    const pid = Number(id);
    if (!pid || Number.isNaN(pid)) return;
    axiosInstance.post("/UrunYorumlari/listele", { urunId: pid, page: 1, pageSize: 1 })
      .then(res => {
        if (res.data?.success) {
          const o = res.data.data?.ozet;
          if (o) setReviewOzet({ ortalama: o.ortalama, toplamAdet: o.toplamAdet });
        }
      }).catch(() => {});
  }, [id]);

  /* ───────────────── Fetch ───────────────── */
  useEffect(() => {
    (async () => {
      const pid = Number(id);
      if (!pid || Number.isNaN(pid)) {
        setErr("Geçersiz ürün.");
        setData(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setErr(undefined);

      try {
        const res = await getProductDetail(pid);

        if (res?.success && res.data) {
          const d: any = res.data;
          const normalized: ProductDetailDto = {
            id: Number(d.id ?? d.Id),
            ad: String(d.ad ?? d.Ad ?? ""),
            paraBirimi: d.paraBirimi ?? d.ParaBirimi ?? "TRY",

            fiyat: Number(d.fiyat ?? d.Fiyat ?? 0),
            listeFiyati: d.listeFiyati ?? d.ListeFiyati ?? null,
            indirimliFiyat: d.indirimliFiyat ?? d.IndirimliFiyat ?? null,
            satisFiyati: d.satisFiyati ?? d.SatisFiyati ?? null,
            indirimAktifMi: d.indirimAktifMi ?? d.IndirimAktifMi ?? null,

            stokAdedi: d.stokAdedi ?? d.StokAdedi ?? null,

            fotoMain: d.fotoMain ?? d.FotoMain ?? null,
            fotoList: d.fotoList ?? d.FotoList ?? null,

            aciklama: d.aciklama ?? d.Aciklama ?? null,
            ozellikler: d.ozellikler ?? d.Ozellikler ?? null,

            varyantId: d.varyantId ?? d.VaryantId ?? null,
            fiyatNet: d.fiyatNet ?? d.FiyatNet ?? null,
            vergiOraniYuzde: d.vergiOraniYuzde ?? d.VergiOraniYuzde ?? null,
            secilenSecenekOzet: d.secilenSecenekOzet ?? d.SecilenSecenekOzet ?? null,
          };

          setData(normalized);
          setActive(0);
          setTab("desc");
        } else {
          setData(null);
          setErr(res?.errorDetail || "Ürün yüklenemedi.");
        }
      } catch (e: any) {
        setData(null);
        setErr(e?.message || "Ürün yüklenemedi.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  /* ───────────────── Derived ───────────────── */
  const images: string[] = useMemo(() => {
    if (!data) return [];
    const arr = [data.fotoMain, ...(data.fotoList || [])].filter(Boolean) as string[];
    return Array.from(new Set(arr));
  }, [data]);

  const P = useMemo(() => (data ? computePricing(data) : null), [data]);
  const descHtml = useMemo(() => sanitizeHtml(data?.aciklama), [data?.aciklama]);

  const outOfStock =
    typeof data?.stokAdedi === "number" ? (data!.stokAdedi as number) <= 0 : false;

  const prev = () => images.length && setActive((i) => (i - 1 + images.length) % images.length);
  const next = () => images.length && setActive((i) => (i + 1) % images.length);

  /* ───────────────── WhatsApp & UI numbers ───────────────── */
  const whatsappPhoneE164 = "+905360381141";
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const havaleFiyat = useMemo(() => {
    const base = P?.sale ?? data?.fiyat ?? 0;
    const v = Math.max(0, base * 0.98);
    return { value: v, text: money(v, P?.currency || data?.paraBirimi || "TRY") };
  }, [P?.sale, P?.currency, data?.fiyat, data?.paraBirimi]);

  const taksitFrom = useMemo(() => {
    const base = P?.sale ?? data?.fiyat ?? 0;
    const v = base / 9;
    return { value: v, text: money(v, P?.currency || data?.paraBirimi || "TRY") };
  }, [P?.sale, P?.currency, data?.fiyat, data?.paraBirimi]);

  /* ───────────────── Actions ───────────────── */
  const onAddToCart = async () => {
    if (!data) return;

    const unitGross = P?.sale ?? data.fiyat ?? 0;
    const varyantId = data.varyantId ?? data.id;

    const fiyatNet =
      typeof data.fiyatNet === "number" && data.fiyatNet > 0 ? data.fiyatNet : unitGross;

    const vergi = typeof data.vergiOraniYuzde === "number" ? data.vergiOraniYuzde : 0;

    const ok = await addItem({
      urunId: Number(data.id),
      varyantId: Number(varyantId),
      urunAdi: data.ad,
      secenekOzeti: data.secilenSecenekOzet ?? undefined,
      adet: qty,
      birimFiyatNet: Number(fiyatNet) || 0,
      vergiOraniYuzde: Number(vergi) || 0,
      satirIndirimNet: 0,
    });

    if (ok) {
      setNote("Ürün sepete eklendi.");
      setTimeout(() => setNote(null), 1800);
    } else {
      setNote("Sepete eklenemedi. Lütfen tekrar deneyin.");
      setTimeout(() => setNote(null), 2200);
    }
  };

  /* ───────────────── Render ───────────────── */
  if (loading) return <SkeletonView />;

  if (err || !data) {
    return (
      <div style={{ backgroundColor: pageBg }}>
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="p-6 rounded-2xl text-center border" style={cardStyle}>
            <div className="text-lg font-semibold mb-2" style={{ color: textPrimary }}>
              Ürün bulunamadı
            </div>
            <div className="text-sm" style={{ color: textSecondary }}>
              {err || "Aradığınız ürün kaldırılmış olabilir."}
            </div>

            <div className="mt-5 flex justify-center">
              <Link
                to="/urunler"
                className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold"
                style={{ ...subtleBorder, color: textPrimary, backgroundColor: cardBg }}
              >
                <ArrowLeft className="w-4 h-4" />
                Ürünlere dön
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const title = data.ad;
  const saleText = P?.saleText || "";
  const normalText = P?.normalText || "";

  const TabBtn = ({ k, label }: { k: TabKey; label: string }) => {
    const activeTab = tab === k;
    return (
      <button
        type="button"
        onClick={() => setTab(k)}
        className="px-0 pb-3 text-sm font-semibold transition whitespace-nowrap"
        style={{
          color: activeTab ? textPrimary : textSecondary,
          borderBottom: activeTab ? `2px solid ${String(accent)}` : "2px solid transparent",
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <>
      <div style={{ backgroundColor: pageBg }}>
        <div className="max-w-7xl mx-auto px-4 py-6 md:py-10">
          {/* Breadcrumb */}
          <div className="mb-4 md:mb-6 flex items-center justify-between gap-3">
            <nav className="text-xs md:text-sm" style={{ color: textSecondary }}>
              <Link to="/" className="hover:underline">
                Anasayfa
              </Link>{" "}
              <span className="mx-1">/</span>
              <Link to="/urunler" className="hover:underline">
                Ürünler
              </Link>{" "}
              <span className="mx-1">/</span>
              <span className="opacity-90">{title}</span>
            </nav>
          </div>

          <div className="grid grid-cols-12 gap-6">
            {/* Left */}
            <section className="col-span-12 md:col-span-7 lg:col-span-7">
              {/* Title + badges */}
              {/* <div className="mb-4">
                <h1 className="text-xl md:text-2xl lg:text-3xl font-extrabold tracking-tight" style={{ color: textPrimary }}>
                  {title}
                </h1>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {P?.isDiscount && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                      style={{
                        backgroundColor: isDarkBg ? "rgba(16,185,129,0.18)" : "rgba(16,185,129,0.12)",
                        color: textPrimary,
                        border: `1px solid ${borderCol}`,
                      }}
                    >
                      <BadgePercent className="w-3.5 h-3.5" />
                      {P.pct ? `%${P.pct} indirim` : "İndirim"}
                    </span>
                  )}

                  {outOfStock ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                      style={{
                        backgroundColor: isDarkBg ? "rgba(244,63,94,0.18)" : "rgba(244,63,94,0.10)",
                        color: textPrimary,
                        border: `1px solid ${borderCol}`,
                      }}
                    >
                      <PackageX className="w-3.5 h-3.5" />
                      Stokta yok
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                      style={{
                        backgroundColor: isDarkBg ? "rgba(37,99,235,0.18)" : "rgba(37,99,235,0.10)",
                        color: textPrimary,
                        border: `1px solid ${borderCol}`,
                      }}
                    >
                      <Truck className="w-3.5 h-3.5" />
                      Hızlı gönderi
                    </span>
                  )}

                  <span
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                    style={{ backgroundColor: "transparent", color: textSecondary, border: `1px solid ${borderCol}` }}
                  >
                    <ShieldCheck className="w-3.5 h-3.5" />
                    Güvenli alışveriş
                  </span>

 
                </div>
              </div> */}

              {/* Gallery */}
              <div className="relative rounded-2xl overflow-hidden border" style={cardStyle}>
                <div className="relative">
                  <div className="aspect-[4/3] w-full bg-white">
                    {images.length ? (
                      <AnimatePresence mode="wait">
                        <motion.img
                          key={images[active] || "noimg"}
                          src={images[active] || ""}
                          alt={title}
                          className="w-full h-full object-contain"
                          initial={{ opacity: 0.35, scale: 1.01 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.22 }}
                          onError={(e) => ((e.target as HTMLImageElement).style.display = "none")}
                        />
                      </AnimatePresence>
                    ) : (
                      <div className="w-full h-full grid place-items-center text-sm" style={{ color: textSecondary }}>
                        Görsel yok
                      </div>
                    )}
                  </div>

                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prev}
                        className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-10 h-10 rounded-full border bg-white shadow-sm"
                        style={{ borderColor: borderCol }}
                        aria-label="Önceki görsel"
                      >
                        <ChevronLeft size={18} />
                      </button>
                      <button
                        onClick={next}
                        className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-10 h-10 rounded-full border bg-white shadow-sm"
                        style={{ borderColor: borderCol }}
                        aria-label="Sonraki görsel"
                      >
                        <ChevronRight size={18} />
                      </button>
                    </>
                  )}

                  {outOfStock && (
                    <div className="absolute inset-0 z-20 grid place-items-center">
                      <div className="absolute inset-0 bg-black/35" />
                      <div className="relative inline-flex items-center gap-2 rounded bg-white px-10 py-2 text-[13px] font-semibold tracking-widest text-slate-900 shadow-lg ring-1 ring-black/5">
                        <PackageX className="w-4 h-4" />
                        TÜKENDİ
                      </div>
                    </div>
                  )}
                </div>

                {/* thumbs */}
                {images.length > 1 && (
                  <div className="p-3 border-t bg-white" style={subtleBorder}>
                    <div className="hidden md:grid grid-cols-6 lg:grid-cols-8 gap-2">
                      {images.slice(0, 12).map((u, i) => (
                        <button
                          key={i}
                          onClick={() => setActive(i)}
                          className={`relative rounded-xl overflow-hidden border h-20 ${
                            i === active ? "ring-2 ring-offset-1" : "hover:opacity-95"
                          }`}
                          style={{
                            borderColor: i === active ? (accent as any) : borderCol,
                            outlineColor: accent as any,
                          }}
                          aria-label={`Görsel ${i + 1}`}
                        >
                          <img src={u} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>

                    <div className="md:hidden flex gap-2 overflow-x-auto snap-x">
                      {images.map((u, i) => (
                        <button
                          key={i}
                          onClick={() => setActive(i)}
                          className={`relative rounded-xl overflow-hidden border flex-none w-24 h-20 snap-start ${
                            i === active ? "ring-2" : ""
                          }`}
                          style={{
                            borderColor: i === active ? (accent as any) : borderCol,
                            outlineColor: accent as any,
                          }}
                          aria-label={`Görsel ${i + 1}`}
                        >
                          <img src={u} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Tabs */}
              <div className="mt-6 border-b flex gap-6 overflow-x-auto" style={subtleBorder}>
                <TabBtn k="desc" label="Ürün Açıklaması" />
                <TabBtn k="delivery" label="Garanti ve Teslimat" />
                {/* <TabBtn k="installments" label="Taksit Seçenekleri" /> */}
                <TabBtn k="comments" label="Yorumlar" />
              </div>

              {/* Tab content */}
              <div className="pt-5">
                {tab === "desc" && (
                  <div className="space-y-6">
                    {/* Açıklama */}
                    <div className="rounded-2xl border bg-white p-4 md:p-5" style={{ borderColor: borderCol }}>
                      <div className="flex items-center justify-between">
                        <h3 className="text-base font-bold" style={{ color: textPrimary }}>
                          Ürün Açıklaması
                        </h3>
                        {!descHtml && (
                          <span className="text-xs" style={{ color: textSecondary }}>
                            Açıklama eklenmemiş
                          </span>
                        )}
                      </div>

                      {descHtml ? (
                        <div
                          className="prose prose-sm md:prose-base max-w-none mt-3"
                          style={{ color: textSecondary }}
                          dangerouslySetInnerHTML={{ __html: descHtml }}
                        />
                      ) : (
                        <div className="mt-3 text-sm" style={{ color: textSecondary }}>
                          Bu ürün için henüz açıklama eklenmedi.
                        </div>
                      )}
                    </div>

                    {/* Özellikler */}
                    {Array.isArray(data.ozellikler) && data.ozellikler.length > 0 && (
                      <div className="rounded-2xl border bg-white p-4 md:p-5" style={{ borderColor: borderCol }}>
                        <h3 className="text-base font-bold mb-4" style={{ color: textPrimary }}>
                          Teknik Özellikler
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {data.ozellikler
                            .slice()
                            .sort((a, b) => (a.siraNo ?? 0) - (b.siraNo ?? 0))
                            .map((o) => (
                              <div
                                key={o.id}
                                className="flex items-start gap-3 rounded-xl border px-3 py-2.5"
                                style={{ borderColor: borderCol }}
                              >
                                <div
                                  className="w-36 shrink-0 text-[11px] font-semibold uppercase tracking-wide"
                                  style={{ color: textSecondary }}
                                >
                                  {o.ozellikAdi}
                                </div>
                                <div className="text-sm font-semibold" style={{ color: textPrimary }}>
                                  {o.ozellikDegeri}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {tab === "delivery" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      {
                        icon: <Truck className="w-5 h-5" />,
                        title: "Hızlı Gönderim",
                        desc: "Siparişler özenle paketlenir ve hızlıca kargoya verilir.",
                      },
                      {
                        icon: <ShieldCheck className="w-5 h-5" />,
                        title: "Güvenli Alışveriş",
                        desc: "Güvenli ödeme ve müşteri desteğiyle sorunsuz alışveriş.",
                      },
                      {
                        icon: <RotateCcw className="w-5 h-5" />,
                        title: "İade & Değişim",
                        desc: "Koşullara uygun şekilde iade/değişim desteklenir.",
                      },
                      {
                        icon: <MessageCircle className="w-5 h-5" />,
                        title: "Canlı Destek",
                        desc: "WhatsApp üzerinden hızlı destek alabilirsiniz.",
                      },
                    ].map((x, i) => (
                      <div key={i} className="rounded-2xl border bg-white p-4" style={{ borderColor: borderCol }}>
                        <div className="flex items-start gap-3">
                          <div
                            className="w-10 h-10 rounded-xl grid place-items-center border bg-white"
                            style={{ borderColor: borderCol, color: textPrimary }}
                          >
                            {x.icon}
                          </div>
                          <div>
                            <div className="text-sm font-bold" style={{ color: textPrimary }}>
                              {x.title}
                            </div>
                            <div className="text-sm mt-1" style={{ color: textSecondary }}>
                              {x.desc}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Eğer taksit tabını açarsan buradaki data’yı kullanabilirsin */}
                    {/* <div className="rounded-2xl border bg-white p-4 md:col-span-2" style={{ borderColor: borderCol }}>
                      <div className="text-sm" style={{ color: textSecondary }}>
                        {taksitFrom.text}’den başlayan taksitlerle.
                      </div>
                    </div> */}
                  </div>
                )}

                {tab === "installments" && (
                  <div className="rounded-2xl border bg-white p-4" style={{ borderColor: borderCol }}>
                    <div className="text-sm" style={{ color: textSecondary }}>
                      {taksitFrom.text}’den başlayan taksitlerle.
                      <div className="mt-2 text-xs opacity-80">
                        Not: Taksit seçenekleri ödeme altyapınıza göre değişebilir.
                      </div>
                    </div>
                  </div>
                )}

                {tab === "comments" && (
                  <ProductReviews
                    urunId={data.id}
                    onOzetLoaded={(o) => setReviewOzet({ ortalama: o.ortalama, toplamAdet: o.toplamAdet })}
                  />
                )}
              </div>
            </section>

            {/* Right purchase */}
            <aside className="col-span-12 md:col-span-5 lg:col-span-5">
              <div className="md:sticky md:top-36 space-y-4">
                <div className="p-5 rounded-2xl border bg-white shadow-sm" style={{ borderColor: borderCol }}>
                  {/* Title */}
                  <div className="flex items-start justify-between gap-3">
                    <h2 className=" font-bold text-lg leading-snug" style={{ color: textPrimary }}>
                      {title}
                    </h2>

                    <div className="flex items-center gap-2 shrink-0">
                      <FavoriteButton urunId={data.id} size={20} className="w-10 h-10 border border-slate-200" />
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    {/* Ortalama yıldız */}
                    {reviewOzet && reviewOzet.toplamAdet > 0 && (
                      <button
                        onClick={() => setTab("comments")}
                        className="inline-flex items-center gap-1.5 hover:opacity-80 transition"
                        title="Yorumlara git"
                      >
                        <Stars value={Math.round(reviewOzet.ortalama)} size={15} />
                        <span className="text-sm font-semibold text-slate-700">
                          {reviewOzet.ortalama.toFixed(1)}
                        </span>
                        <span className="text-xs text-slate-400">
                          ({reviewOzet.toplamAdet} yorum)
                        </span>
                      </button>
                    )}

                    {P?.isDiscount && (
                      <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-white"
                            style={{ backgroundColor: "#059669" }}>
                        <BadgePercent className="h-3.5 w-3.5" />
                        {P.pct ? `%${P.pct}` : "İndirim"}
                      </span>
                    )}
                  </div>

                  {/* Price row */}
                  <div className="mt-3 flex items-end gap-3">
                    <div className="text-3xl  leading-none" style={{ color: textPrimary }}>
                      {saleText}
                    </div>

                    {P?.isDiscount && (
                      <div className="text-sm line-through pb-[2px]" style={{ color: textSecondary }}>
                        {normalText}
                      </div>
                    )}
                  </div>

                  {/* Stock / info */}
                  <div className="mt-2 text-xs" style={{ color: textSecondary }}>
                    {typeof data.stokAdedi === "number" ? (
                      <span>
                      </span>
                    ) : (
                      <span>
                        Stok bilgisi: <b style={{ color: textPrimary }}>—</b>
                      </span>
                    )}
                  </div>

                  {/* Havale */}
                  <div className="mt-3 text-sm" style={{ color: textSecondary }}>
                    <div className="inline-flex items-center gap-2">
                      <span className="h-5 w-[3px] rounded-full" style={{ backgroundColor: accent as any }} />
                      <span>
                        Havale/EFT ile <b style={{ color: textPrimary }}>{havaleFiyat.text}</b>
                      </span>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="mt-4 border-t" style={{ borderColor: borderCol }} />

                  {/* Qty */}
                  <div className="mt-4 flex items-center justify-between gap-4">
  {/* Left label */}
  <div className="min-w-0">
    <div className="text-sm font-semibold" style={{ color: textPrimary }}>
      Adet
    </div>
    <div className="text-xs" style={{ color: textSecondary }}>
      Sepete eklenecek miktar
    </div>
  </div>

  {/* Right stepper */}
  <div
    className="inline-flex items-center rounded-xl border bg-white shadow-sm overflow-hidden"
    style={{ borderColor: borderCol }}
  >
    <button
      type="button"
      onClick={() => setQty((q) => Math.max(1, q - 1))}
      className="h-11 w-11 inline-flex items-center justify-center transition hover:bg-slate-50 active:bg-slate-100"
      style={{ color: textPrimary }}
      aria-label="Azalt"
    >
      <Minus size={18} />
    </button>

    <div className="h-11 w-px" style={{ backgroundColor: borderCol }} />

    <input
      value={qty}
      onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
      className="h-11 w-16 text-center text-sm font-semibold outline-none"
      style={{ color: textPrimary }}
      type="number"
      min={1}
    />

    <div className="h-11 w-px" style={{ backgroundColor: borderCol }} />

    <button
      type="button"
      onClick={() => setQty((q) => q + 1)}
      className="h-11 w-11 inline-flex items-center justify-center transition hover:bg-slate-50 active:bg-slate-100"
      style={{ color: textPrimary }}
      aria-label="Arttır"
    >
      <Plus size={18} />
    </button>
  </div>
</div>

                  {/* CTAs */}
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
  {/* Sepete Ekle */}
  <button
    onClick={onAddToCart}
    disabled={cartLoading || outOfStock}
    className="h-12 inline-flex items-center col-span-1 justify-center gap-2 rounded-xl px-4 font-extrabold bg-orange-500 text-white
               shadow-sm transition
               hover:opacity-95 active:scale-[0.99]
               disabled:opacity-55 disabled:cursor-not-allowed disabled:active:scale-100"
    
  >
    Sepete Ekle
  </button>

  {/* WhatsApp */}
  <a
    href={`https://wa.me/${whatsappPhoneE164.replace("+", "")}?text=${encodeURIComponent(
      `Merhaba, ${data.ad} ürünü için sipariş vermek istiyorum.\nAdet: ${qty}\nLink: ${shareUrl}`
    )}`}
    target="_blank"
    rel="noreferrer"
    aria-disabled={outOfStock}
    className={`h-12 inline-flex items-center justify-center gap-2 col-span-2 rounded-xl px-4 font-extrabold text-white
                shadow-sm transition
                hover:opacity-95 active:scale-[0.99]
                ${outOfStock ? "pointer-events-none opacity-55" : ""}`}
    style={{ backgroundColor: "#16a34a" }}
  >
    <MessageCircle className="w-5 h-5" />
    <span className="whitespace-nowrap">WhatsApp ile Sipariş</span>
  </a>
</div>

                  {/* Trust row */}
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <div className="flex flex-col items-center gap-1 text-xs py-2">
                      <Truck className="w-5 h-5" style={{ color: textPrimary }} />
                      <span style={{ color: textSecondary }}>Hızlı Gönderi</span>
                    </div>
                    <div className="flex flex-col items-center gap-1 text-xs py-2">
                      <ShieldCheck className="w-5 h-5" style={{ color: textPrimary }} />
                      <span style={{ color: textSecondary }}>Güvenli</span>
                    </div>
                    <div className="flex flex-col items-center gap-1 text-xs py-2">
                      <RotateCcw className="w-5 h-5" style={{ color: textPrimary }} />
                      <span style={{ color: textSecondary }}>İade</span>
                    </div>
                  </div>

                  {!!note && (
                    <div
                      className="mt-4 text-sm px-3 py-2 rounded-xl border bg-white"
                      style={{ borderColor: borderCol, color: textSecondary }}
                    >
                      {note}
                    </div>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* Mobile bottom CTA */}
      <div className="md:hidden fixed bottom-20 left-0 right-0 z-40">
        <div className="mx-auto max-w-7xl px-3 pb-[calc(env(safe-area-inset-bottom,0px))]">
          <div className="rounded-2xl border bg-white shadow-xl" style={{ borderColor: borderCol }}>
            <div className="flex items-center gap-3 p-3">
              <div className="flex-1">
                <div className="text-xs" style={{ color: textSecondary }}>
                  Toplam
                </div>
                <div className="text-lg font-extrabold" style={{ color: textPrimary }}>
                  {money((P?.sale ?? data.fiyat) * qty, P?.currency || data.paraBirimi || "TRY")}
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-9 h-9 inline-flex items-center justify-center rounded border bg-white"
                  style={{ borderColor: borderCol, color: textPrimary }}
                  aria-label="Azalt"
                >
                  <Minus size={16} />
                </button>

                <input
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
                  className="w-14 h-9 text-center rounded border bg-white outline-none"
                  style={{ borderColor: borderCol, color: textPrimary }}
                  type="number"
                  min={1}
                />

                <button
                  type="button"
                  onClick={() => setQty((q) => q + 1)}
                  className="w-9 h-9 inline-flex items-center justify-center rounded border bg-white"
                  style={{ borderColor: borderCol, color: textPrimary }}
                  aria-label="Arttır"
                >
                  <Plus size={16} />
                </button>
              </div>

              <button
                onClick={onAddToCart}
                disabled={cartLoading || outOfStock}
                className="inline-flex items-center justify-center rounded-xl px-4 py-3 font-extrabold text-white disabled:opacity-60"
                style={{ backgroundColor: accent as any }}
              >
                Sepete Ekle
              </button>
            </div>

            <div className="px-3 pb-3 -mt-1">
              <a
                href={`https://wa.me/${whatsappPhoneE164.replace("+", "")}?text=${encodeURIComponent(
                  `Merhaba, ${data.ad} ürünü için sipariş vermek istiyorum.\nAdet: ${qty}\nLink: ${shareUrl}`
                )}`}
                target="_blank"
                rel="noreferrer"
                className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-extrabold text-white ${
                  outOfStock ? "pointer-events-none opacity-60" : ""
                }`}
                style={{ backgroundColor: "#16a34a" }}
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp ile Sipariş Ver
              </a>

              {outOfStock && (
                <div className="pt-2 text-xs" style={{ color: textSecondary }}>
                  Stokta yok — sepete eklenemez.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/* ---------------- Skeleton View ---------------- */
const SkeletonView: React.FC = () => (
  <div style={{ backgroundColor: "transparent" }}>
    <div className="max-w-7xl mx-auto px-4 py-6 md:py-10">
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-7 lg:col-span-8">
          <div className="h-8 w-2/3 rounded mb-3 animate-pulse bg-slate-200" />
          <div className="aspect-[4/3] w-full rounded-2xl animate-pulse bg-slate-200" />
          <div className="mt-3 grid grid-cols-6 gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-20 rounded-2xl animate-pulse bg-slate-200" />
            ))}
          </div>
          <div className="mt-6 h-28 rounded-2xl animate-pulse bg-slate-200" />
          <div className="mt-4 h-40 rounded-2xl animate-pulse bg-slate-200" />
        </div>
        <div className="col-span-12 md:col-span-5 lg:col-span-4">
          <div className="h-56 rounded-2xl animate-pulse bg-slate-200" />
          <div className="mt-4 h-40 rounded-2xl animate-pulse bg-slate-200" />
        </div>
      </div>
    </div>
  </div>
);

export default ProductDetail;